/**
 *  Date    : 2021/08/19
 *  Author  : lbb1811
 *  Declare : LoginModal
 */

import React, { useRef, useEffect, useMemo } from 'react';
import { InputItem, Toast, Picker, List } from 'antd-mobile-v2';
import { useSetState, useCountDown } from 'ahooks';

import Modal from '@/components/Modal/Modal';
import { ALPHABET, NOOP, NFT_KXD } from '@/constants/common';
import messagecenter from '@/api/messagecenter';
import usercenter from '@/api/usercenter';
import readercenter from '@/api/readercenter';
import GlobalData from '@/utils/globalData';
import { useActions, useSelector } from '@/utils/redux';
import { addTrack, TRACK_TYPE } from '@/business/spider/track';
import Authorize from '@/business/authorize';
import Utils from '@/utils/utils';
import authUtils from '@/business/authorize/authUtils';
import Popup from '@/components/Popup';
import ShowToast, { CustomToast } from '@/components/ToastModal/ShowToast';

import './LoginModal.scss';

export default function LoginModal(props) {

  const { onClose = NOOP, showLogin = true, propsLogin, isPhoneLogin = false } = props;

  const { officialAccountsId, wechatUserId } = GlobalData.getUserInfo();

  const { getUserPhoneNumber } = useActions(({ reader }) => reader);

  const { hasPhoneNumber, routeSpm, loginModalClassname } = useSelector(state => state.reader);

  const [state, setState] = useSetState({
    showLogin: showLogin,
    showServiceBtn: false,
    showServiceQrCode: false,
    areaList: [],
    phoneAreaCode: '+86',
    sValue: ['+86,中国'],
    validateCode: '',
    showPhone: false, // 手机登录
    agreed: false // 协议勾选状态
  });

  const phoneNumber = useRef('');
  const validateCode = useRef('');
  const sending = useRef(false);

  const closedRef = useRef(false);

  const IS_NFT = sessionStorage.getItem(NFT_KXD);

  const [countdown, setTargetDate] = useCountDown();

  // 用来解决刚进入时手机号登录弹窗会一闪的现象
  useEffect(() => {
    setTimeout(() => {
      setState({
        hadMount: true
      });
    }, 500);
  }, []);

  useEffect(() => {
    usercenter.getPhoneArea().then(data => {
      const list = ALPHABET.split('').map(key => data[key]).filter(v => v);
      const areaList = [].concat(...list).map(item => {
        return {
          label: (
            <div className="area-code_QTuzze__" key={item.phoneAreaCode}>
              <div className="area-name">{item.name}</div>
              <div className="area-code">{item.phoneAreaCode}</div>
            </div>
          ),
          value: item.phoneAreaCode + ',' + item.name // Picker组件是以value为key渲染的
        };
      });
      setState({
        areaList
      });
    });
  }, []);

  useEffect(() => {
    if(hasPhoneNumber) closedRef.current = true;
    if(!closedRef.current && wechatUserId && !hasPhoneNumber && state.showLogin) {
      // spm:1502, subSpm 0:进入界面(展示按钮); 1:登录(确定); 2:取消; 3:登录成功; 4:登录失败; 5:点击按钮 -1:未知bug
      addTrack({
        type: TRACK_TYPE.LOGIN,
        spm: routeSpm,
        subSpm: '0'
      });
    }
  }, [wechatUserId, hasPhoneNumber, state.showLogin]);

  const updatePhoneNumber = (data) => {
    setState({ phoneNumber: data });
    phoneNumber.current = data;
  };

  const onChangeArea = (e) => {
    const [v = ''] = e;
    const code = v.split(',')[0];
    setState({ sValue: e, phoneAreaCode: code });
  };

  const onChangePhoneNumber = (data) => {
    updatePhoneNumber(data.replace(/\s/g, ''));
  };

  const onDelete = () => {
    updatePhoneNumber('');
  };

  const onChangeVerifyCode = (e) => {
    setState({ validateCode: e });
    validateCode.current = e;
  };

  const onClickGetCode = () => {
    if(countdown > 0) return;

    if(!phoneNumber.current) {
      CustomToast({ title: '请输入手机号', type: 'none' });
      return;
    }
    if(state.phoneAreaCode === '+86' && !MOBILE_REG.test(phoneNumber.current)) {
      CustomToast({ title: '手机号格式不正确', type: 'none' });
      return;
    }
    if(countdown === 0) {
      setTargetDate(Date.now() + 60000); // 60s
      let requestParams = {
        phone: phoneNumber.current,
        phoneAreaCode: +state.phoneAreaCode,
        state: 3
      };
      messagecenter.sendPhoneMessage(requestParams)
        .catch(err => {
          const msg = `${err.message}`.replace('CAPTURED: ', '');
          Toast.info(msg);
        }).then(() => {
          CustomToast({ title: '验证码已发送', type: 'none' });
        });
      // spm:1502, subSpm 0:进入界面(展示按钮); 1:登录(确定); 2:取消; 3:登录成功; 4:登录失败; 5:点击按钮 -1:未知bug
      addTrack({
        type: TRACK_TYPE.LOGIN,
        spm: routeSpm,
        subSpm: '5',
        phone: phoneNumber.current,
        phoneAreaCode: +state.phoneAreaCode
      });
    }
  };

  const onLogin = () => {
    // 检查协议是否勾选
    if(!state.agreed) {
      return CustomToast({ title: '请阅读并同意《隐私协议》和《用户协议》', type: 'none' });
    }
    if(!phoneNumber.current) {
      return CustomToast({ title: '请输入手机号', type: 'none' });
    }
    if(state.phoneAreaCode === '+86' && !MOBILE_REG.test(phoneNumber.current)) {
      CustomToast({ title: '手机号格式不正确', type: 'none' });
      return;
    }
    if(validateCode.current.length !== 4) {
      return CustomToast({ title: '请输入验证码', type: 'none' });
    }

    if(phoneNumber.current && validateCode.current) {
      if(propsLogin) {
        propsLogin(phoneNumber.current, validateCode.current, state.phoneAreaCode);
        return;
      }
      sending.current = true;
      Toast.loading('登录中...');
      // spm:1502, subSpm 0:进入界面(展示按钮); 1:登录(确定); 2:取消; 3:登录成功; 4:登录失败; 5:点击按钮 -1:未知bug
      addTrack({
        type: TRACK_TYPE.LOGIN,
        spm: routeSpm,
        subSpm: '1',
        phone: phoneNumber.current,
        phoneAreaCode: +state.phoneAreaCode
      });
      // 校验
      readercenter.phoneAuthorizeUser({
        phone: phoneNumber.current,
        validate: validateCode.current,
        offId: authUtils.config.DEFAULT_PHONE_OFFICIAL_ACCOUNTS_ID
      }).then((data) => {
        // 更新合并用户手机号
        if(Number(data.officialAccountsId) > 0 && Number(data.wechatUserId) > 0) {
          readercenter.updateUserByOpenId({
            phoneNumber: phoneNumber.current,
            officialAccountsId: data.officialAccountsId || officialAccountsId,
            wechatUserId: data.wechatUserId
          }).then(async (result) => {
            sending.current = false;
            // spm:1502, subSpm 0:进入界面(展示按钮); 1:登录(确定); 2:取消; 3:登录成功; 4:登录失败; 5:点击按钮 -1:未知bug
            await addTrack({
              type: TRACK_TYPE.LOGIN,
              spm: routeSpm,
              subSpm: '3',
              phone: phoneNumber.current,
              phoneAreaCode: +state.phoneAreaCode
            });
            // 绑定手机号成功
            Toast.info('登录成功', 1.5, () => {
              onClose(true);
              getUserPhoneNumber({ officialAccountsId });
            });
          }).catch(err => {
            Toast.info(err.message);
            sending.current = false;
          });
        } else {
          Toast.hide();
          sending.current = false;
        }

        // 用户登录
        if(data && data.wechatUserId) {
          // 存在第三方授权，静默授权微信id关联手机号
          if(Utils.browser().wechat || Utils.browser().douyin || Utils.browser().aliPay) {
            Authorize.startAuthorize({ snsapiBase: true, phone: phoneNumber.current });
          } else {
            authUtils.saveCookieUser(data);
          }
        }

      }).catch(err => {
        let error = err.message;
        if(error.indexOf('CAPTURED:') !== -1) {
          error = error.replace('CAPTURED:', '');
        }
        Toast.info(error);
        sending.current = false;
      });
    }
  };

  const wxAuthor = () => {
    Authorize.startAuthorize();
  };

  const onCloseModal = () => {
    // spm:1502, subSpm 0:进入界面(展示按钮); 1:登录(确定); 2:取消; 3:登录成功; 4:登录失败; 5:点击按钮 -1:未知bug
    addTrack({
      type: TRACK_TYPE.LOGIN,
      spm: routeSpm,
      subSpm: '2',
      phone: phoneNumber.current,
      phoneAreaCode: +state.phoneAreaCode
    });
    onClose();
    setState({ showLogin: false });
  };

  // 判断登录方式 signInWith
  const loginType = useMemo(() => {
    let webApp = Object.keys(AUTHOR_METHOD);
    // 兼容之前获取手机号业务
    if(!authUtils.getCookieUser().isTempUser) {
      webApp = webApp.filter(v => (v !== 'wechat'));
    }
    const [browser = ''] = webApp.filter(v => !!Utils.browser()[v]);
    // nft 只存在手机号授权 | 不存在第三方授权展示手机登录
    // isPhoneLogin 测评h5环境只支持手机号登陆
    if(JSON.parse(IS_NFT) || !browser || isPhoneLogin) {
      setState({ showPhone: true });
    }
    return AUTHOR_METHOD[browser] || {};
  }, [IS_NFT]);

  // 没有 wechatUserId ，先不渲染
  // 有手机号了，不渲染
  if((!wechatUserId || hasPhoneNumber) || !state.hadMount) return null;
  const disabled = !!phoneNumber.current && !!validateCode.current;
  return (
    <div className={`LoginModalWrap ${loginModalClassname}`} onClick={() => setState({ showLogin: true })}>
      <Popup
        title="登录注册"
        visible={state.showLogin}
        maskClosable={false}
        onClose={onCloseModal}
        bodyClassName="mem-login-modal"
      >
        {
          state.showPhone &&
          <div className="login-wrap">
            {
              loginType.name &&
              <div className="login-title f18 dis-flex">
                {!isPhoneLogin && <span className="ic ic-arrow-copy text-gray9" onClick={() => setState({ showPhone: false })} />}
                {/* <span className="flex1">手机号登录</span> */}
              </div>
            }
            <div className="phone-number-wrap">
              <div className="area-code">
                {state.phoneAreaCode}
                <List className="area-pick-list" style={{ display: state.showLogin ? 'block' : 'none' }}>
                  <Picker
                    data={state.areaList}
                    title="选择地区"
                    cols={1}
                    value={state.sValue || ''}
                    onChange={onChangeArea}
                  >
                    <List.Item arrow="horizontal">AREA_CODE</List.Item>
                  </Picker>
                </List>
              </div>
              <InputItem
                type="phone"
                placeholder="请输入手机号"
                value={state.phoneNumber}
                onChange={onChangePhoneNumber}
                className="phone-input"
              />
              {
                state.phoneNumber ?
                  <div className="close-icon-wrap" onClick={onDelete}>
                    <div className="close-icon-x" />
                  </div>
                  :
                  <div className="close-icon-wrap" />
              }
            </div>

            <div className="verify-code-wrap">
              <div className="code-wrap">
                <InputItem
                  type="number"
                  maxLength={CODE_LENGTH}
                  placeholder="输入短信验证码"
                  onChange={onChangeVerifyCode}
                  className={`verify-code ${state.validateCode.length ? 'has-code' : ''}`}
                />
                {/* <div className="bottom-icon-wrap">
                {new Array(CODE_LENGTH).fill('').map((_, i) => <span className="icon-line" key={i} />)}
              </div> */}
              </div>
              <div
                className={`get-code-button ${countdown === 0 ? '' : 'disabled'}`}
                onClick={onClickGetCode}
              >
                {countdown === 0 ? '获取验证码' : `${Math.round(countdown / 1000)}秒后可重发`}
              </div>
            </div>
            {/* <div className="feedback-tips">
              <span>收不到验证码？</span>
              <span className="feedback-btn" onClick={() => setState({ showServiceBtn: true })}>点我反馈</span>
            </div> */}
            <div className="mem-login-tips" onClick={() => setState({ showServiceQrCode: true })}>收不到验证码？<span>联系客服<i className="mem-login-tips-icon" /></span></div>
            <div className="agreement-checkbox">
              <div
                className={`checkbox ${state.agreed ? 'checked' : ''}`}
                onClick={() => setState({ agreed: !state.agreed })}
              />
              <span className="agreement-text">
                我已阅读并同意
                <a href="https://oss.5rs.me/oss/uploadfe/html/rays_yinsi.html" target="_blank" rel="noopener noreferrer" className="agreement-link">《隐私协议》</a>
                和
                <a href="https://oss.5rs.me/oss/uploadfe/html/rays_fuwu.html" target="_blank" rel="noopener noreferrer" className="agreement-link">《用户协议》</a>
              </span>
            </div>
            <div className={`login-btn ${disabled ? '' : 'disabled'}`} onClick={onLogin}>登 录</div>
            {/* {
              state.showServiceBtn &&
              <div className="service-tips-wrap">
                {
                  SELF_CHECK_TIPS.map((text, index) => (
                    <div className="tips-line" key={index}>
                      <span className="seq-icon">{index + 1}</span>
                      <span className="tips-text">{text}</span>
                    </div>
                  ))
                }
                <div className="btn-tips-wrap">
                  <span className="btn-tips-text">已经确认？</span>
                  <span className="contact-btn" onClick={() => setState({ showServiceQrCode: true })}>联系客服</span>
                </div>
              </div>
            } */}
          </div>
        }

        {
          !state.showPhone && loginType.name &&
          <div className="login_method">
            <div className="login_method-title">登录方式</div>
            <div className="dis-flex login_method_list">
              <div className="dis-flex login_method_item login_method_phone dis-flex col-flex center-flex" onClick={() => setState({ showPhone: true })}>
                <img className="login_method_image" src={PHONE_METHOD.image} />
                <span>{PHONE_METHOD.name}</span>
              </div>
              <div className="login_method_item login_method_weChat dis-flex col-flex center-flex" onClick={wxAuthor}>
                <img className="login_method_image" src={loginType.image} />
                <span>{loginType.name}</span>
              </div>
            </div>
          </div>
        }

      </Popup>

      <Modal
        className="default-modal customer-modal"
        showModal={state.showServiceQrCode}
      >
        <div className="customer-box">
          <img className="customer-img" src={CUSTOMER_IMG} />
          <div className="customer-content">
            <img className="wx-img" src={WX_IMG} />
            <p className="wx-desc">长按识别二维码</p>
            <p className="wx-desc small">我来解决你的问题</p>
          </div>
          <i className="close2" onClick={() => setState({ showServiceQrCode: false })} />
        </div>
      </Modal>
      <ShowToast />
    </div>
  );
}

/** 验证码长度 */
const CODE_LENGTH = 4;
const MOBILE_REG = /^1[^12][0-9]{9}$/;

// const SELF_CHECK_TIPS = [
//   '请先核对手机号码是否输入正确',
//   '手机信号差，或状态异常，可以换个地方或重启手机',
//   '可能设置了垃圾短信拦截，请在垃圾短信里查找',
//   '换个时间段，尝试再次获取验证码'
// ];
// const CUSTOMER_IMG = 'https://file.5rs.me/oss/uploadfe/png/1731d94afc309fc1445ba1e89ba912f6.png';
const CUSTOMER_IMG = 'https://oss.5rs.me/oss/uploadfe/png/694d381c7ad32aee40a957fd490549a3.png';
const WX_IMG = 'https://file.5rs.me/oss/uploadfe/png/a621c76045bd7f83fa8bd12e91ad197d.png';

const PHONE_METHOD = {
  name: '手机验证码',
  image: 'https://oss.5rs.me/oss/uploadfe/png/96db234772978e2cadec175de5ec30a8.png?x-oss-process=image/format,webp'
};

const AUTHOR_METHOD = {
  wechat: {
    name: '微信',
    image: 'https://oss.5rs.me/oss/uploadfe/png/bbf7d332509150b794d8aca5bf0ec42b.png?x-oss-process=image/format,webp'
  },
  qq: {
    name: 'QQ',
    image: 'https://oss.5rs.me/oss/uploadfe/png/ebdaa5c58bfb8448397e0bc2bd7fb14a.png?x-oss-process=image/format,webp'
  },
  douyin: {
    name: '抖音',
    image: 'https://oss.5rs.me/oss/uploadfe/png/c7875b604617ee4962d9d1cedf90d86a.png?x-oss-process=image/format,webp'
  },
  aliPay: {
    name: '支付宝',
    image: 'https://oss.5rs.me/oss/uploadfe/png/694b916c7840b37a09a2398e0338ac44.png?x-oss-process=image/format,webp'
  }
};
